import './QRScanner.css';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { BrowserMultiFormatReader } from '@zxing/library';
import React, { useEffect, useRef, useState } from 'react';

import { CernQrCodeValue } from '../../api/types';
import { AdamsError } from '../../utils/errors';
import { getValueFromQr } from '../../utils/get-value-from-qr';
import { useEndpointsData } from '../EndpointsData/EndpointsDataProvider';

const styles = {
    cameraSelectDropdown: { minWidth: 250 },
};

interface QRScannerProps {
    onDetect: (arg: CernQrCodeValue) => void;
    onClose: () => void;
    onError: (err: AdamsError) => void;
    translations: Record<string, string>;
}

export const QRScanner: React.FC<QRScannerProps> = ({ onDetect, onClose, onError, translations }) => {
    const codeReader = useRef(new BrowserMultiFormatReader()).current;
    const [videoInputDevices, setVideoInputDevices] = useState<MediaDeviceInfo[]>([]);
    const [camera, setCamera] = useState('');
    const { endpointsData } = useEndpointsData();
    const qrCodesData = endpointsData.settings.qrCodeURLs;

    useEffect(() => {
        navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'environment' } }).then((environmentStream) => {
            if (environmentStream.getVideoTracks().length > 0) {
                codeReader
                    .listVideoInputDevices()
                    .then((devices) => {
                        if (devices.length > 0) {
                            setVideoInputDevices(devices);
                            const cam = localStorage.getItem('camera');
                            const c = devices.find((device) => device.deviceId === cam);
                            if (c) setCamera(c.deviceId);
                            else {
                                const environmentCamera = devices.find(
                                    (device) => device.label === environmentStream.getVideoTracks()[0].label
                                )!;
                                setCamera(environmentCamera.deviceId);
                            }
                        }
                    })
                    .catch(onError);
            }
        });

        return () => {
            codeReader.reset();
        };
    }, [codeReader, onError]);

    const handleClose = () => {
        codeReader.reset();
        onClose();
    };

    //used by cypress only
    const detectFromImage = (image: string) =>
        codeReader
            .decodeFromImageUrl(image)
            .then((result) => result.getText())
            .then((code) => getValueFromQr(code, qrCodesData, translations.ERROR_NOT_CERN_QR_CODE))
            .then(onDetect);

    const handleError = (error: AdamsError) => {
        const image = document.querySelector('#cypress-image-test-mock')?.getAttribute('src');
        if (image) {
            detectFromImage(image).catch(() => {
                detectFromImage(image).catch((err) => {
                    codeReader.reset();
                    onError(err);
                });
            });
        } else {
            codeReader.reset();
            onError(error);
        }
    };

    const scan = (cam = camera) => {
        codeReader
            .decodeOnceFromVideoDevice(cam, 'video')
            .then((result) => result.getText())
            .then((code) => getValueFromQr(code, qrCodesData, translations.ERROR_NOT_CERN_QR_CODE))
            .then(onDetect)
            .catch(handleError);
    };

    const handleCameraChange = (cam: string) => {
        codeReader.reset();
        setCamera(cam);
        scan(cam);
        localStorage.setItem('camera', cam);
    };

    const startScanner = () => scan();

    return (
        <Dialog
            open={true}
            fullScreen
            TransitionProps={{
                onEntered: startScanner,
            }}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            {videoInputDevices?.length > 1 && (
                <DialogTitle>
                    <TextField
                        sx={styles.cameraSelectDropdown}
                        value={camera}
                        onChange={(e) => handleCameraChange(e.target.value)}
                        select
                        label="Choose the camera"
                    >
                        {videoInputDevices.map((videoInputDevice) => (
                            <MenuItem key={videoInputDevice.deviceId} value={videoInputDevice.deviceId}>
                                {videoInputDevice.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogTitle>
            )}
            <DialogContent id="video-dialog">
                <video autoPlay muted playsInline id="video" />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary" size="large">
                    {translations.GLOBAL_CANCEL_BUTTON}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
